'use client';

import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { isOtPerfEnabled, OT_CONSENT_COOKIE_NAME } from '@ab-inbev-eu/utils/onetrust';

import { getAppInfo, getCookie } from '../utils/functions.client';
import { publicConfig } from '../utils/config';

const { IS_DEV_MODE, DATADOG, ENVIRONMENT, BUILD_VERSION } = publicConfig();

// background requests that don't affect what the user sees. Without this they keep the page active
// and inflate the RUM Loading Time metric (it waits until network activity settles)
// don't add requests rendering depends on (translations, campaign assets) - that would hide real slowness
const EXCLUDED_ACTIVITY_HOSTS = [
  'braze.eu',
  'braze.com',
  'segment.io',
  'segment.com',
  'googletagmanager.com',
  'google-analytics.com',
  'onetrust.com',
  'cookielaw.org',
];

function isExcludedActivityUrl(url: string) {
  try {
    const { hostname, pathname } = new URL(url, window.location.origin);
    if (pathname.endsWith('/healthz') || pathname.endsWith('/sse')) return true;
    // locize saveMissing/update POSTs; translation GETs share the host and must stay tracked
    if (hostname === 'api.locize.app' && /^\/(missing|update)\//.test(pathname)) return true;
    return EXCLUDED_ACTIVITY_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
}

export default function DatadogConnector(props: { userId?: string }) {
  const initDatadog = () => {
    if (
      !datadogRum.getInitConfiguration() &&
      !IS_DEV_MODE &&
      DATADOG.APPLICATION_ID &&
      DATADOG.CLIENT_TOKEN &&
      isOtPerfEnabled(decodeURIComponent(getCookie(OT_CONSENT_COOKIE_NAME) ?? ''))
    ) {
      const { country, channel } = getAppInfo();
      datadogRum.setGlobalContextProperty('app_country', country);
      datadogRum.setGlobalContextProperty('dtc_app_name', channel);

      datadogRum.init({
        applicationId: DATADOG.APPLICATION_ID,
        clientToken: DATADOG.CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: DATADOG.SERVICE,
        env: ENVIRONMENT,
        version: BUILD_VERSION,
        sessionSampleRate: DATADOG.SESSION_SAMPLE_RATE,
        sessionReplaySampleRate: DATADOG.SESSION_REPLAY_SAMPLE_RATE,
        trackUserInteractions: DATADOG.TRACK_USER_INTERACTION,
        trackResources: DATADOG.TRACK_RESOURCES,
        trackLongTasks: DATADOG.TRACK_LONG_TASKS,
        defaultPrivacyLevel: DATADOG.DEFAULT_PRIVACY_LEVEL,
        excludedActivityUrls: [isExcludedActivityUrl],

        allowedTracingUrls: DATADOG.ENABLE_RUM_TRACING ? [(url) => url.startsWith(window.location.origin)] : undefined,
      });
    }
  };

  useEffect(() => {
    datadogRum.setUser({
      id: props.userId,
    });
  }, [props.userId]);

  useEffect(() => {
    initDatadog();
    window.addEventListener('consent.onetrust', initDatadog);
    return () => {
      window.removeEventListener('consent.onetrust', initDatadog);
    };
  }, []);

  return null;
}
